import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import BarChartIcon from "@mui/icons-material/BarChart";
import PeopleIcon from "@mui/icons-material/People";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useAuth, API_BASE } from "./AuthContext";
import { useSearchParams } from "react-router-dom";
import { useMatomo } from "react-matomo";

const ACCENT = "#3D5A80";
const DARK = "#293241";
const GOLD = "#E9B44C";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  href?: string;
}

function FeatureCard({ icon, title, description, href }: FeatureCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1.5px solid #c8d8e8`,
        borderRadius: 3,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        backgroundColor: "#fff",
        height: "100%",
        boxSizing: "border-box",
        transition: "box-shadow 0.15s, border-color 0.15s",
        "&:hover": { boxShadow: "0 4px 16px rgba(61,90,128,0.13)", borderColor: ACCENT },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            color: "#fff",
            backgroundColor: ACCENT,
            borderRadius: "50%",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight="bold" color={DARK}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, lineHeight: 1.55 }}>
        {description}
      </Typography>
      {href && (
        <Button
          href={href}
          variant="text"
          size="small"
          sx={{
            mt: 0.5,
            color: ACCENT,
            alignSelf: "flex-start",
            textTransform: "none",
            fontWeight: "bold",
            px: 0,
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          Open Board ↗
        </Button>
      )}
    </Paper>
  );
}

const FEATURES = [
  {
    icon: "stuff",
    title: "psStuff+ Leaderboard",
    description: "Full pitcher stuff board with psStuff+ for every pitch type. Filter by pitch type, level, team, and more.",
    href: "/plus/stuff/",
  },
  {
    icon: "bar",
    title: "Pitch Type Performance Board",
    description: "Hitter and pitcher pitch-type leaderboards showing run value, wOBA, whiff rate, and outcomes broken down by pitch type.",
    href: "/plus/hitter-pitches/",
  },
  {
    icon: "compare",
    title: "Splits Percentiles",
    description: "Side-by-side LHP/RHP (or LHB/RHB) percentile bars for every player. See exactly where a hitter or pitcher ranks vs each handedness within their level.",
    href: "/plus/splits",
  },
  {
    icon: "stuff",
    title: "Catcher Framing Board",
    description: "MiLB catcher framing leaderboard using delta run expectancy. See framing runs saved, rate per 100 opportunities, stolen strikes, and missed strikes.",
    href: "/plus/framing/",
  },
  {
    icon: "people",
    title: "MLB Comparison Tool",
    description: "Find which current MLB hitters a prospect\u0027s approach stats most resemble. Z-score similarity search across K%, BB%, EV, barrel rate, and more.",
  },
  {
    icon: "compare",
    title: "MLB Context",
    description: "See how a prospect\u0027s percentile grades translate at the MLB level \u2014 side-by-side sliders comparing prospect stats to MLB average and top performers.",
  },  {
    icon: "list",
    title: "Custom Leaderboards",
    description: "Build and save your own leaderboards with any group of players and any combination of stats columns. Perfect for watchlists and player comparisons.",
    href: "/plus/custom-leaderboards",
  },];

function PlanToggle({ plan, setPlan }: { plan: "monthly" | "yearly"; setPlan: (p: "monthly" | "yearly") => void }) {
  return (
    <Box sx={{ display: "inline-flex", backgroundColor: "#1e2d3f", borderRadius: "999px", p: "4px", mb: 2.5 }}>
      {(["monthly", "yearly"] as const).map((p) => (
        <Button
          key={p}
          size="small"
          onClick={() => setPlan(p)}
          sx={{
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: "bold",
            px: 2.5,
            py: 0.6,
            minWidth: 90,
            backgroundColor: plan === p ? GOLD : "transparent",
            color: plan === p ? DARK : "#aabbcc",
            "&:hover": { backgroundColor: plan === p ? GOLD : "#2a3d55" },
            transition: "background-color 0.15s",
          }}
        >
          {p === "monthly" ? "Monthly" : "Yearly"}
        </Button>
      ))}
    </Box>
  );
}

function PriceDisplay({ plan }: { plan: "monthly" | "yearly" }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 0.5,
        mb: 2,
      }}
    >
      <Typography variant="h2" fontWeight="bold" sx={{ color: GOLD, lineHeight: 1 }}>
        {plan === "monthly" ? "$5" : "$45"}
      </Typography>
      <Typography variant="body1" sx={{ color: "#aabbcc" }}>
        {plan === "monthly" ? "/ month" : "/ year"}
      </Typography>
      {plan === "yearly" && (
        <Typography variant="body2" sx={{ color: "#7ec8a0", ml: 1 }}>
          save $15
        </Typography>
      )}
    </Box>
  );
}

export default function PSPlus(props: any) {
  const { user, loading, login, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const { trackEvent } = useMatomo();
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState("");
  const [plan, setPlan] = useState<"monthly" | "yearly">("monthly");
  const [cancelConfirming, setCancelConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [cancelledAt, setCancelledAt] = useState<number | null>(null);

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError("");
    try {
      const token = localStorage.getItem("ps_token");
      const res = await fetch(`${API_BASE}/cancel-subscription`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) {
        setCancelledAt(data.cancel_at ?? null);
        setCancelConfirming(false);
        refreshUser();
      } else {
        setCancelError(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setCancelError("Network error. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const justSucceeded = searchParams.get("success") === "1";
  const wasCancelled = searchParams.get("cancelled") === "1";

  // Server state is the source of truth so a reload still shows "cancelled";
  // the local value only covers the gap before refreshUser() lands.
  const pendingCancel = cancelledAt ?? user?.cancel_at ?? null;
  const cancelDate = pendingCancel
    ? new Date(pendingCancel * 1000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "";

  // Report the Stripe conversion to Matomo. Keyed on the checkout session id so
  // a reload or a bookmarked success URL cannot double-count it.
  const checkoutSessionId = searchParams.get("session_id") || "";
  const purchasedPlan = searchParams.get("plan") === "yearly" ? "yearly" : "monthly";
  useEffect(() => {
    if (!justSucceeded) return;
    const key = `ps_plus_conversion_${checkoutSessionId || "unknown"}`;
    if (checkoutSessionId && localStorage.getItem(key)) return;
    trackEvent({
      category: "PS Plus",
      action: "Subscribed",
      name: purchasedPlan,
      value: purchasedPlan === "yearly" ? "45" : "5",
    });
    if (checkoutSessionId) localStorage.setItem(key, "1");
  }, [justSucceeded, checkoutSessionId, purchasedPlan, trackEvent]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    setSubscribeError("");
    try {
      const token = localStorage.getItem("ps_token");
      const res = await fetch(`${API_BASE}/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setSubscribeError(data.error || "Something went wrong. Please try again.");
        setSubscribing(false);
      }
    } catch {
      setSubscribeError("Network error. Please try again.");
      setSubscribing(false);
    }
  };

  const iconMap: Record<string, React.ReactNode> = {
    stuff: <SportsBaseballIcon sx={{ fontSize: 18 }} />,
    bar: <BarChartIcon sx={{ fontSize: 18 }} />,
    compare: <CompareArrowsIcon sx={{ fontSize: 18 }} />,
    people: <PeopleIcon sx={{ fontSize: 18 }} />,
    list: <ViewListIcon sx={{ fontSize: 18 }} />,
  };

  return (
    <Box sx={{ backgroundColor: "#eaf6fb", minHeight: "80vh", pb: 8 }}>
      <title>PS Plus - Prospect Savant</title>
      {/* ── Hero ── */}
      <Box
        sx={{
          background: `linear-gradient(160deg, ${DARK} 0%, #1a2e47 100%)`,
          color: "#fff",
          pt: { xs: 6, sm: 8 },
          pb: { xs: 6, sm: 8 },
          px: { xs: 2, sm: 4 },
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <StarIcon sx={{ color: GOLD, fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" sx={{ letterSpacing: 2, color: "#fff" }}>
            PS Plus
          </Typography>
          <StarIcon sx={{ color: GOLD, fontSize: 28 }} />
        </Box>
        <Typography variant="h6" sx={{ color: "#8fa8c0", maxWidth: 520, mx: "auto", mb: 4, fontWeight: 400 }}>
          Advanced analytics tools for serious prospect watchers
        </Typography>

        {loading ? (
          <CircularProgress sx={{ color: GOLD }} />
        ) : user && user.is_plus ? (
          /* ── Subscribed ── */
          <Box>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Avatar src={user.picture} alt={user.name} sx={{ width: 44, height: 44, border: `2px solid ${GOLD}` }} />
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ color: "#fff", fontWeight: "bold" }}>{user.name}</Typography>
                <Typography variant="caption" sx={{ color: "#8fa8c0" }}>{user.email}</Typography>
              </Box>
            </Box>
            <Chip
              icon={<StarIcon sx={{ color: `${GOLD} !important`, fontSize: "1rem !important" }} />}
              label="PS Plus Active"
              sx={{ backgroundColor: "#2a3d55", color: GOLD, fontWeight: "bold", fontSize: "0.9rem", px: 1, mb: 3 }}
            />
            {justSucceeded && (
              <Alert severity="success" sx={{ maxWidth: 420, mx: "auto", mb: 2.5, borderRadius: 2 }}>
                Subscription confirmed! You now have full PS Plus access.
              </Alert>
            )}
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
              {[
                { href: "/plus/stuff/", label: "Stuff+ Board" },
                { href: "/plus/hitter-pitches/", label: "Pitch Type Board" },
                { href: "/plus/splits", label: "Splits" },
                { href: "/plus/framing/", label: "Framing Board" },
                { href: "/plus/custom-leaderboards", label: "Custom Leaderboards" },
              ].map(({ href, label }) => (
                <Button
                  key={label}
                  href={href}
                  variant="outlined"
                  size="small"
                  sx={{ borderColor: GOLD, color: GOLD, textTransform: "none", fontWeight: "bold", borderRadius: 2, "&:hover": { backgroundColor: "#2a3d55", borderColor: GOLD } }}
                >
                  {label} ↗
                </Button>
              ))}
            </Box>
            {/* ── Cancel subscription ── */}
            <Box sx={{ mt: 3 }}>
              {pendingCancel ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                  <Button
                    data-testid="cancel-state"
                    variant="outlined"
                    size="small"
                    disabled
                    startIcon={<EventBusyIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "0.85rem",
                      borderRadius: 2,
                      px: 2,
                      "&.Mui-disabled": { borderColor: "#e57373", color: "#e57373" },
                    }}
                  >
                    Subscription cancelled
                  </Button>
                  <Alert severity="info" sx={{ maxWidth: 420, mx: "auto", borderRadius: 2 }}>
                    Your subscription will remain active until <strong>{cancelDate}</strong>.
                    No further charges will be made.
                  </Alert>
                </Box>
              ) : cancelConfirming ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: "#8fa8c0" }}>
                    Your access continues until the end of the current billing period.
                  </Typography>
                  {cancelError && (
                    <Alert severity="error" sx={{ maxWidth: 420, mx: "auto", borderRadius: 2 }}>
                      {cancelError}
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={cancelling}
                      onClick={() => { setCancelConfirming(false); setCancelError(""); }}
                      sx={{ borderColor: "#8fa8c0", color: "#8fa8c0", textTransform: "none", borderRadius: 2, "&:hover": { backgroundColor: "#2a3d55", borderColor: "#8fa8c0" } }}
                    >
                      Keep subscription
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={cancelling}
                      onClick={handleCancel}
                      sx={{ borderColor: "#e57373", color: "#e57373", textTransform: "none", borderRadius: 2, "&:hover": { backgroundColor: "#3a1a1a", borderColor: "#e57373" } }}
                    >
                      {cancelling ? <CircularProgress size={16} sx={{ color: "#e57373" }} /> : "Yes, cancel"}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button
                  data-testid="cancel-button"
                  variant="outlined"
                  size="small"
                  onClick={() => setCancelConfirming(true)}
                  sx={{
                    borderColor: "#e57373",
                    color: "#e57373",
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    borderRadius: 2,
                    px: 2,
                    "&:hover": { backgroundColor: "#3a1a1a", borderColor: "#ef9a9a", color: "#ef9a9a" },
                  }}
                >
                  Cancel subscription
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          /* ── Not subscribed (logged in or logged out) ── */
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            {user && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                <Avatar src={user.picture} alt={user.name} sx={{ width: 36, height: 36 }} />
                <Typography sx={{ color: "#8fa8c0", fontSize: "0.9rem" }}>Signed in as {user.email}</Typography>
              </Box>
            )}
            {wasCancelled && (
              <Alert severity="info" sx={{ maxWidth: 420, mx: "auto", mb: 2, borderRadius: 2 }}>
                Payment cancelled — no charge was made.
              </Alert>
            )}
            {subscribeError && (
              <Alert severity="error" sx={{ maxWidth: 420, mx: "auto", mb: 2, borderRadius: 2 }}>
                {subscribeError}
              </Alert>
            )}

            <PlanToggle plan={plan} setPlan={setPlan} />
            <PriceDisplay plan={plan} />

            {user ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  disabled={subscribing}
                  onClick={handleSubscribe}
                  startIcon={subscribing ? undefined : <LockOpenIcon />}
                  sx={{
                    backgroundColor: GOLD,
                    color: DARK,
                    fontWeight: "bold",
                    fontSize: "1rem",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 4,
                    py: 1.2,
                    boxShadow: "0 4px 14px rgba(233,180,76,0.4)",
                    "&:hover": { backgroundColor: "#d4a03c", boxShadow: "0 4px 18px rgba(233,180,76,0.55)" },
                    "&:disabled": { backgroundColor: "#a07830", color: "#555" },
                  }}
                >
                  {subscribing
                    ? <CircularProgress size={22} sx={{ color: DARK }} />
                    : `Subscribe — ${plan === "monthly" ? "$5/month" : "$45/year"}`}
                </Button>
                <Typography variant="caption" sx={{ color: "#6a8a9e", mt: 1.5 }}>
                  Secure checkout via Stripe · Cancel anytime
                </Typography>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  onClick={login}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#333",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 4,
                    py: 1.2,
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  Sign in to Subscribe
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* ── Features Grid ── */}
      <Box sx={{ maxWidth: 920, mx: "auto", px: { xs: 2, sm: 3 }, mt: 6 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ color: DARK, textAlign: "center", mb: 0.5 }}>
          What You Get
        </Typography>
        <Typography variant="body2" sx={{ color: "#6a8a9e", textAlign: "center", mb: 4 }}>
          Exclusive tools for MiLB analysis
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            gap: 2.5,
            alignItems: "stretch",
          }}
        >
          {FEATURES.map((f) => (
            <FeatureCard key={f.title} icon={iconMap[f.icon]} title={f.title} description={f.description} href={f.href} />
          ))}
        </Box>

        {/* ── Why PS Plus ── */}
        <Box
          sx={{
            mt: 5,
            p: { xs: 2.5, sm: 3.5 },
            backgroundColor: "#fff",
            border: "1.5px solid #c8d8e8",
            borderRadius: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" color={DARK} mb={2}>
            Why PS Plus?
          </Typography>
          {[
            "Covers hosting & data server costs to keep the site running",
            "Supports continued development of new tools and data coverage",
            "One-person project — your support directly funds the work",
            "No ads, no tracking, no paywalled basic prospect info",
          ].map((line) => (
            <Box key={line} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ color: ACCENT, fontSize: 20, mt: "1px", flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {line}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── Bottom CTA ── */}
        {!loading && !user?.is_plus && (
          <Box
            sx={{
              mt: 5,
              p: { xs: 3, sm: 4 },
              background: `linear-gradient(135deg, ${DARK} 0%, #1a2e47 100%)`,
              borderRadius: 3,
              textAlign: "center",
              color: "#fff",
            }}
          >
            <Typography variant="h5" fontWeight="bold" mb={1}>
              Ready to upgrade?
            </Typography>
            <Typography variant="body2" sx={{ color: "#8fa8c0", mb: 3 }}>
              {plan === "monthly" ? "$5/month" : "$45/year (save $15)"} · Cancel anytime
            </Typography>
            <PlanToggle plan={plan} setPlan={setPlan} />
            <Box sx={{ mt: 1 }}>
              {user ? (
                <Button
                  variant="contained"
                  size="large"
                  disabled={subscribing}
                  onClick={handleSubscribe}
                  startIcon={subscribing ? undefined : <LockOpenIcon />}
                  sx={{
                    backgroundColor: GOLD,
                    color: DARK,
                    fontWeight: "bold",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 4,
                    py: 1.2,
                    fontSize: "1rem",
                    boxShadow: "0 4px 14px rgba(233,180,76,0.4)",
                    "&:hover": { backgroundColor: "#d4a03c" },
                  }}
                >
                  {subscribing
                    ? <CircularProgress size={22} sx={{ color: DARK }} />
                    : `Subscribe — ${plan === "monthly" ? "$5/month" : "$45/year"}`}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  onClick={login}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#333",
                    fontWeight: "bold",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 4,
                    py: 1.2,
                    fontSize: "1rem",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  Sign in to Subscribe
                </Button>
              )}
            </Box>
            <Typography variant="caption" sx={{ display: "block", color: "#6a8a9e", mt: 1.5 }}>
              Questions? Email duke@prospectsavant.com
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
